import React, { useEffect, useRef, useState } from 'react'
import Text from '../../../Text'
import styles from './styles.module.css'
import { getPathFromSlug } from '../../../../utils/urls'
import { regionalHref, normalizeInternalHref } from '../../../../utils/regions'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import SmartLink from '../../../SmartLink'
import { Collapse, createTheme, ThemeProvider } from '@mui/material'

const theme = createTheme({
  breakpoints: {
    values: {
      xxs: 0,
      md: 900,
    }
  }
})

// Gap kept between a wide (grouped) dropdown and the viewport edges.
const VIEWPORT_GUTTER = 16

// Intrinsic size of every public/images/nav-hardware thumbnail (all cropped to
// the same 252x156 canvas); passed as width/height so the box is reserved.
const NAV_THUMB_WIDTH = 252
const NAV_THUMB_HEIGHT = 156

// Split nav items into the grouped ("mega") layout: ungrouped items before the
// first grouped one are lead links, ungrouped items after it are tail links,
// and grouped items become columns in order of first appearance.
function groupItems(items) {
  const lead = []
  const tail = []
  const groups = []
  for (const item of items) {
    if (!item.navGroup) {
      (groups.length ? tail : lead).push(item)
      continue
    }
    let group = groups.find(g => g.title === item.navGroup)
    if (!group) {
      group = { title: item.navGroup, items: [] }
      groups.push(group)
    }
    group.items.push(item)
  }
  return { lead, tail, groups }
}

function NavDropdown(props) {
  const { items, show } = props
  const pageRegion = useUrlRegion()
  const wrapperRef = useRef(null)
  const [offset, setOffset] = useState(null)

  const grouped = items.some(item => item.navGroup)

  // Desktop placement (on mobile the dropdown stays in the hamburger flow):
  // every dropdown sits flush under the header bar, and the grouped one, being
  // much wider than its nav button, is centred on the viewport. The wrapper is
  // positioned relative to the nav <li>, hence the offsets.
  useEffect(() => {
    if (!show) return
    const place = () => {
      const el = wrapperRef.current
      if (!el || !el.parentElement) return
      if (window.innerWidth < 900) {
        setOffset(null)
        return
      }
      const anchor = el.parentElement.getBoundingClientRect()
      // The header BAR (the <header>'s child div that NavScrollController pins
      // with position: fixed once the page scrolls), not the <header> itself:
      // that wrapper stays in the page flow, so after scrolling its bottom is
      // far above the viewport and the dropdown opened off screen.
      const bar = el.closest('header > div') || el.closest('header')
      const top = bar ? bar.getBoundingClientRect().bottom - anchor.top : null
      const left = grouped
        ? Math.max(VIEWPORT_GUTTER, (window.innerWidth - el.offsetWidth) / 2) - anchor.left
        : null
      setOffset({ left, top })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [grouped, show])

  // prevent "bubbling" of onClick in parent
  // NOTE: in our case "bubbling" occurs when a link in a dropdown is clicked,
  // thus initiating the redirect and closing the dropdown simultaneously, which leads to the dropdown closing before the redirect actually happens
  const onClick = (e) => e.stopPropagation()

  const hrefFor = (item) => {
    // item.link may be an absolute internal vrs.racing URL; normalize
    // it so commerce links are region-scoped and content stays bare.
    const externalLink = normalizeInternalHref(item.link, pageRegion)
    return externalLink || regionalHref(getPathFromSlug(item.route.slug.current), pageRegion)
  }

  const title = (item) => (
    <Text type={'regular'} highlight="G2" highlightSx="color: #E11D48;" highlightExactMatch>{item.titleOverride || item.title}</Text>
  )

  const renderItem = (item) => (
    <React.Fragment key={item._key}>
      { item.hasSeparatorTop && <div className={styles.separator} role="separator" /> }
      <SmartLink
        href={hrefFor(item)}
        className={item.navImage ? `${styles.linkTextWrapper} ${styles.withImage}` : styles.linkTextWrapper}
        onClick={(e) => onClick(e)}
        role="menuitem"
      >
        {/* task #67: item.icon is a ResolvedImage descriptor; icon takes .src + reserves box via aspectRatio */}
        {/* eslint-disable-next-line local-rules/prefer-img-component, local-rules/img-requires-dimensions */}
        { item.icon && <img className={styles.icon} src={item.icon?.src} style={{ aspectRatio: item.icon?.aspectRatio || '1 / 1' }} loading="eager" decoding="async" data-lazy-exempt alt=""/> }
        {/* navImage is a ResolvedImage descriptor (a pre-cropped thumbnail from public/images/nav-hardware) */}
        {/* eslint-disable-next-line local-rules/prefer-img-component */}
        { item.navImage && <span className={styles.thumb}><img src={item.navImage.placeholderUrl || item.navImage.src} width={NAV_THUMB_WIDTH} height={NAV_THUMB_HEIGHT} loading="lazy" decoding="async" alt=""/></span> }
        {title(item)}
      </SmartLink>
      { item.hasSeparatorBottom && <div className={styles.separator} role="separator" /> }
    </React.Fragment>
  )

  // A grouped item with a render is a product row; without one it is a plain
  // text link (e.g. Accessories -> shop).
  const renderMegaItem = (item) => (
    <SmartLink
      key={item._key}
      href={hrefFor(item)}
      className={item.navImage ? styles.megaItem : styles.megaTextLink}
      onClick={(e) => onClick(e)}
      role="menuitem"
    >
      {/* eslint-disable-next-line local-rules/prefer-img-component */}
      { item.navImage && <span className={styles.megaThumb}><img src={item.navImage.placeholderUrl || item.navImage.src} width={NAV_THUMB_WIDTH} height={NAV_THUMB_HEIGHT} loading="lazy" decoding="async" alt=""/></span> }
      {title(item)}
    </SmartLink>
  )

  const renderMegaLink = (item) => (
    <SmartLink
      key={item._key}
      href={hrefFor(item)}
      className={styles.megaLink}
      onClick={(e) => onClick(e)}
      role="menuitem"
    >
      <Text type={'heading-8'}>{item.titleOverride || item.title}</Text>
    </SmartLink>
  )

  const renderMega = () => {
    const { lead, tail, groups } = groupItems(items)
    return (
      <div className={`${styles.dropdown} ${styles.mega}`} role="menu">
        { lead.length > 0 && <div className={styles.megaLinks}>{lead.map(renderMegaLink)}</div> }
        <div className={styles.megaGroups}>
          {
            groups.map(group => (
              <div key={group.title} className={styles.megaGroup} role="group" aria-label={group.title}>
                <div className={styles.megaGroupHead}>
                  <Text type={'heading-6'}>{group.title}</Text>
                </div>
                {group.items.map(renderMegaItem)}
              </div>
            ))
          }
        </div>
        { tail.length > 0 && <div className={`${styles.megaLinks} ${styles.megaLinksTail}`}>{tail.map(renderMegaLink)}</div> }
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      className={grouped ? `${styles.dropdownWrapper} ${styles.megaWrapper}` : styles.dropdownWrapper}
      style={offset ? { ...(offset.left != null && { left: offset.left }), ...(offset.top != null && { top: offset.top }) } : undefined}
    >
      <ThemeProvider theme={theme}>
        <Collapse
          in={show}
          timeout={{ enter: 500, exit: 250 }}
          sx={{
            // the panel is rounded and carries its own shadow
            boxShadow: 'none'
          }}
        >
          {
            grouped ? renderMega() : (
              <div className={styles.dropdown} role="menu">
                {items.map(renderItem)}
              </div>
            )
          }
        </Collapse>
      </ThemeProvider>
    </div>
  )
}

export default NavDropdown
